import { CheckIcon, EnvelopeIcon, UserGroupIcon, UserPlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Avatar } from '@heroui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BsPeople } from "react-icons/bs";
import { ChatRoom, DetailedContactInviteResponseDto } from '../../types/types';
import noChatsEmptyIcon from '../../assets/chat/noChatsEmpty.svg';
import noGroupsEmptyIcon from '../../assets/chat/noGroupsEmpty.svg';
import noInvitesEmptyIcon from '../../assets/chat/noInvitesEmpty.svg';
import { formatTimestamp } from '../../utils/utilMethods';
import { ChatEmptyState } from './chatEmptyState';

interface ChatSidebarProps {
    activeTab: 'chats' | 'groups' | 'invites';
    setActiveTab: React.Dispatch<React.SetStateAction<'chats' | 'groups' | 'invites'>>;
    inviteSubTab: 'myInvites' | 'allRequests';
    setInviteSubTab: React.Dispatch<React.SetStateAction<'myInvites' | 'allRequests'>>;
    contacts: any;
    loading: boolean;
    chatRooms: any;
    filteredChatRooms: ChatRoom[];
    invites: any;
    myInvites: any;
    invitesLoading: boolean;
    selectedUser: any;
    selectedGroup: any;
    selectedInvite: any;
    handleSelectContact: (c: any) => void;
    handleSelectGroup: (g: ChatRoom) => void;
    handleSelectInvite: (invite: DetailedContactInviteResponseDto) => void;
    setShowGroupModal: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedGroup: React.Dispatch<React.SetStateAction<any>>;
    handleFetchMyInvites: () => void;
    handleFetchAllRequests: () => void;
    getLastMessageDisplay: (c: any) => string;
    getLastMessageDisplayForChatRoom: (lastMessage: any) => string;
    formatText: (text: string) => string;
    handleAcceptInvite?: (id: number) => void;
    handleDeclineInvite?: (id: number) => void;
    loggedInUserName?: string;
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({
    activeTab,
    setActiveTab,
    inviteSubTab,
    contacts,
    loading,
    chatRooms,
    filteredChatRooms,
    invites,
    myInvites,
    invitesLoading,
    selectedUser,
    selectedGroup,
    selectedInvite,
    handleSelectContact,
    handleSelectGroup,
    handleSelectInvite,
    setShowGroupModal,
    setSelectedGroup,
    handleFetchMyInvites,
    handleFetchAllRequests,
    getLastMessageDisplay,
    getLastMessageDisplayForChatRoom,
    handleAcceptInvite,
    handleDeclineInvite,
    loggedInUserName,
}) => {
    const { t } = useTranslation('chat');

    const renderInvitesList = () => {
        if (invitesLoading) {
            return (
                <div className="px-3 flex flex-col gap-1 animate-pulse">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="p-3 rounded-[20px] flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-slate-200 shrink-0" />
                            <div className="flex-1 min-w-0 space-y-1.5">
                                <div className="h-4 w-3/5 bg-slate-200 rounded" />
                                <div className="h-3 w-4/5 bg-slate-100 rounded" />
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        const currentInvites = inviteSubTab === 'myInvites' ? myInvites : invites;
        const contactedKey = inviteSubTab === 'myInvites' ? 'contacted' : 'contactedBy';
        const filteredInvites: DetailedContactInviteResponseDto[] = currentInvites?.inviteResponseDtos || [];

        if (!currentInvites || currentInvites.contactedProfilesCount === 0) {
            return (
                <ChatEmptyState
                    icon={noInvitesEmptyIcon}
                    title={t('noInvitesFound', 'No invites found')}
                    subtitle={t('invitesWillAppearHere', 'Invites will appear here')}
                />
            );
        }

        return (
            <div className="px-3 flex flex-col gap-0.5">
                {filteredInvites.map((invite: DetailedContactInviteResponseDto) => {
                    const contacted = invite[contactedKey as keyof DetailedContactInviteResponseDto];
                    const isSelected = selectedInvite?.id === invite.id;

                    const isPending = invite.status === 'PENDING';
                    // Same rule as before: the sender of an invite can't accept their own invite.
                    const canAccept = invite.contactedBy?.email !== loggedInUserName;

                    return (
                        <div
                            key={invite.id}
                            onClick={() => handleSelectInvite(invite)}
                            className={`p-3 rounded-[20px] transition-colors cursor-pointer flex items-center gap-3 ${
                                isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                            }`}
                        >
                            <div className="relative shrink-0">
                                <Avatar
                                    src={(contacted as any).profileImage}
                                    name={`${(contacted as any).firstName} ${(contacted as any).lastName}`}
                                    className="w-9 h-9 text-xs"
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <span
                                    className="block text-sm font-medium font-['Plus_Jakarta_Sans'] text-gray-900 truncate leading-5"
                                    title={`${(contacted as any).firstName} ${(contacted as any).lastName} · ${formatTimestamp(invite.contactedAt)}`}
                                >
                                    {(contacted as any).firstName} {(contacted as any).lastName}
                                </span>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                                <button
                                    type="button"
                                    disabled={!isPending}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeclineInvite?.(invite.id);
                                    }}
                                    className="h-7 px-2.5 rounded-2xl flex items-center gap-1 bg-red-100 hover:bg-red-200/80 text-red-600 text-xs font-medium font-['Plus_Jakarta_Sans'] leading-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <XMarkIcon className="w-3 h-3" strokeWidth={2.5} />
                                    <span>{t('decline', 'Decline')}</span>
                                </button>
                                {canAccept && (
                                    <button
                                        type="button"
                                        disabled={!isPending}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleAcceptInvite?.(invite.id);
                                        }}
                                        className="h-7 px-2.5 rounded-2xl flex items-center gap-1 bg-gray-100 hover:bg-gray-200/80 text-blue-700 text-xs font-medium font-['Plus_Jakarta_Sans'] leading-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <CheckIcon className="w-3 h-3" strokeWidth={2.5} />
                                        <span>{t('accept', 'Accept')}</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="w-full lg:w-[360px] xl:w-[420px] 2xl:w-[480px] shrink-0 bg-white border-r border-gray-100 flex flex-col overflow-hidden py-4 h-full">
            {/* Figma Tabs Segmented Control */}
            <div className="px-6 pb-4">
                <h1 className="hidden lg:block text-xl font-bold text-gray-900 font-['Plus_Jakarta_Sans'] pb-3">Chat</h1>
                <div className="bg-gray-100 rounded-3xl p-1 h-10 flex items-center gap-0.5">
                    <button
                        type="button"
                        onClick={() => setActiveTab('chats')}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-3xl text-sm font-medium font-['Plus_Jakarta_Sans'] leading-5 transition-all ${
                            activeTab === 'chats'
                                ? 'bg-white text-gray-900 shadow-[0px_2px_8px_0px_rgba(0,0,0,0.06)]'
                                : 'text-gray-500 hover:text-gray-800'
                        }`}
                    >
                        <span>{t('chatsTab', 'Chats')}</span>
                        {contacts && contacts?.contactedProfilesCount > 0 && (
                            <span className="bg-blue-600 text-white text-[10px] font-medium rounded-xl min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
                                {contacts?.contactedProfilesCount > 99 ? '99+' : contacts?.contactedProfilesCount}
                            </span>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('groups')}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-3xl text-sm font-medium font-['Plus_Jakarta_Sans'] leading-5 transition-all ${
                            activeTab === 'groups'
                                ? 'bg-white text-gray-900 shadow-[0px_2px_8px_0px_rgba(0,0,0,0.06)]'
                                : 'text-gray-500 hover:text-gray-800'
                        }`}
                    >
                        <span>{t('groupsTab', 'Groups')}</span>
                        {chatRooms && chatRooms?.contactedProfilesCount > 0 && (
                            <span className="bg-blue-600 text-white text-[10px] font-medium rounded-xl min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
                                {chatRooms?.contactedProfilesCount > 99 ? '99+' : chatRooms?.contactedProfilesCount}
                            </span>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('invites')}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-3xl text-sm font-medium font-['Plus_Jakarta_Sans'] leading-5 transition-all ${
                            activeTab === 'invites'
                                ? 'bg-white text-gray-900 shadow-[0px_2px_8px_0px_rgba(0,0,0,0.06)]'
                                : 'text-gray-500 hover:text-gray-800'
                        }`}
                    >
                        <span>{t('invitesTab', 'Invites')}</span>
                        {invites && invites?.contactedProfilesCount > 0 && (
                            <span className="bg-blue-600 text-white text-[10px] font-medium rounded-xl min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
                                {invites.contactedProfilesCount > 99 ? '99+' : invites.contactedProfilesCount}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto no-scrollbar [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {activeTab === 'chats' && (
                    loading ? (
                        <div className="px-3 flex flex-col gap-1 animate-pulse">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="p-3 rounded-[20px] flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-full bg-slate-200 shrink-0" />
                                    <div className="flex-1 min-w-0 space-y-1.5">
                                        <div className="h-4 w-3/5 bg-slate-200 rounded" />
                                        <div className="h-3 w-4/5 bg-slate-100 rounded" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : !contacts?.contactDetailsResponseDtos?.length ? (
                        <ChatEmptyState
                            icon={noChatsEmptyIcon}
                            title={t('noChatsFound', 'No chats found')}
                            subtitle={t('startNewChat', 'Start new chat')}
                        />
                    ) : (
                        <div className="px-3 flex flex-col gap-0.5">
                            {contacts.contactDetailsResponseDtos.map((c: any) => {
                                const isSelected = selectedUser?.profileDto.id === c.profileDto.id;
                                return (
                                    <div
                                        key={c.profileDto.id}
                                        onClick={() => handleSelectContact(c)}
                                        className={`p-3 rounded-[20px] transition-colors cursor-pointer flex items-center gap-3 ${
                                            isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                                        }`}
                                    >
                                        <div className="relative shrink-0">
                                            <Avatar
                                                src={c.profileDto.profileImage}
                                                name={`${c.profileDto.firstName} ${c.profileDto.lastName}`}
                                                className="w-9 h-9 text-xs"
                                            />
                                            {c.profileDto.verified && (
                                                <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-white rounded-full overflow-hidden flex items-center justify-center">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 48 48"
                                                        className="w-full h-full"
                                                    >
                                                        <polygon
                                                            fill="#42a5f5"
                                                            points="29.62,3 33.053,8.308 39.367,8.624 39.686,14.937 44.997,18.367 42.116,23.995 45,29.62 39.692,33.053 39.376,39.367 33.063,39.686 29.633,44.997 24.005,42.116 18.38,45 14.947,39.692 8.633,39.376 8.314,33.063 3.003,29.633 5.884,24.005 3,18.38 8.308,14.947 8.624,8.633 14.937,8.314 18.367,3.003 23.995,5.884"
                                                        />
                                                        <polygon
                                                            fill="#fff"
                                                            points="21.396,31.255 14.899,24.76 17.021,22.639 21.428,27.046 30.996,17.772 33.084,19.926"
                                                        />
                                                    </svg>
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between mb-0.5">
                                                <span className="text-sm font-medium font-['Plus_Jakarta_Sans'] text-gray-900 truncate leading-5">
                                                    {c.profileDto.firstName} {c.profileDto.lastName}
                                                </span>
                                                {c.unreadMessageCount > 0 && (
                                                    <span className="bg-blue-600 text-white text-[10px] font-medium rounded-xl min-w-[18px] h-[18px] flex items-center justify-center px-1.5 ml-2">
                                                        {c.unreadMessageCount > 99 ? '99+' : c.unreadMessageCount}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs font-normal font-['Plus_Jakarta_Sans'] text-gray-500 truncate leading-4">
                                                {getLastMessageDisplay(c)}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )
                )}

                {/* Groups Tab */}
                {activeTab === 'groups' && (
                    <>
                        {/* Create Group Button */}
                        <div className="px-6 pb-3">
                            <button
                                type="button"
                                className="h-8 px-3 py-1.5 bg-gray-100 hover:bg-gray-200/80 rounded-2xl flex items-center gap-1.5 text-gray-800 text-sm font-medium font-['Plus_Jakarta_Sans'] leading-5 transition-all"
                                onClick={() => {
                                    setSelectedGroup(null);
                                    setShowGroupModal(true);
                                }}
                            >
                                <UserGroupIcon className="w-4 h-4 text-gray-700" />
                                <span>{t('createNewGroup', 'Create new group')}</span>
                            </button>
                        </div>

                        {loading ? (
                            <div className="px-3 flex flex-col gap-1 animate-pulse">
                                {Array.from({ length: 4 }).map((_, i) => (
                                    <div key={i} className="p-3 rounded-[20px] flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-full bg-slate-200 shrink-0" />
                                        <div className="flex-1 min-w-0 space-y-1.5">
                                            <div className="h-4 w-3/5 bg-slate-200 rounded" />
                                            <div className="h-3 w-2/5 bg-slate-100 rounded" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : filteredChatRooms.length === 0 ? (
                            <ChatEmptyState
                                icon={noGroupsEmptyIcon}
                                title={t('noGroupFound', 'No group found')}
                                subtitle={t('createNewGroupHint', 'Create new group')}
                            />
                        ) : (
                            <div className="px-3 flex flex-col gap-0.5">
                                {filteredChatRooms.map((g: ChatRoom) => (
                                    <div
                                        key={g.id}
                                        onClick={() => handleSelectGroup(g)}
                                        className={`p-3 rounded-[20px] transition-colors cursor-pointer flex items-center gap-3 ${
                                            selectedGroup?.id === g.id ? 'bg-blue-50' : 'hover:bg-gray-50'
                                        }`}
                                    >
                                        <div className="relative shrink-0">
                                            {g.profileImage ? (
                                                <Avatar src={g.profileImage} name={g.name} className="w-9 h-9 text-xs" />
                                            ) : (
                                                <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 text-base">
                                                    <BsPeople />
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between mb-0.5">
                                                <span className="text-sm font-medium font-['Plus_Jakarta_Sans'] text-gray-900 truncate leading-5">
                                                    {g.name}
                                                </span>
                                                {g.unreadMessages > 0 && (
                                                    <span className="bg-blue-600 text-white text-[10px] font-medium rounded-xl min-w-[18px] h-[18px] flex items-center justify-center px-1.5 ml-2">
                                                        {g.unreadMessages > 99 ? '99+' : g.unreadMessages}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs font-normal font-['Plus_Jakarta_Sans'] text-gray-500 truncate leading-4">
                                                {getLastMessageDisplayForChatRoom(g.lastMessageDetail)}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {/* Invites Tab */}
                {activeTab === 'invites' && (
                    <>
                        {/* Invites Sub-tabs */}
                        <div className="px-6 pb-3">
                            <div className="bg-gray-100 rounded-3xl p-1 h-9 flex items-center gap-0.5">
                                <button
                                    type="button"
                                    onClick={handleFetchAllRequests}
                                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1 rounded-3xl text-sm font-medium font-['Plus_Jakarta_Sans'] leading-5 transition-all ${
                                        inviteSubTab === 'allRequests'
                                            ? 'bg-white text-gray-900 shadow-[0px_2px_8px_0px_rgba(0,0,0,0.06)]'
                                            : 'text-gray-500 hover:text-gray-800'
                                    }`}
                                >
                                    <UserPlusIcon className="w-3.5 h-3.5" />
                                    <span>{t('allRequests', 'All request')}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={handleFetchMyInvites}
                                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1 rounded-3xl text-sm font-medium font-['Plus_Jakarta_Sans'] leading-5 transition-all ${
                                        inviteSubTab === 'myInvites'
                                            ? 'bg-white text-gray-900 shadow-[0px_2px_8px_0px_rgba(0,0,0,0.06)]'
                                            : 'text-gray-500 hover:text-gray-800'
                                    }`}
                                >
                                    <EnvelopeIcon className="w-3.5 h-3.5" />
                                    <span>{t('myInvites', 'My invites')}</span>
                                </button>
                            </div>
                        </div>
                        {renderInvitesList()}
                    </>
                )}
            </div>
        </div>
    );
};

